'use client';

import { mutate, preload } from 'swr';
import { swrKeys } from './config';
import { getBrowserClient } from '@/lib/integrations/supabase/client';
import { getCustomersWithActivity } from '@/lib/features/data/customers';

/**
 * Check if we should prefetch based on network conditions.
 * Returns true for fast connections, false for slow connections to save data.
 */
function shouldPrefetch(): boolean {
  // Server-side: always prefetch
  if (typeof window === 'undefined') return true;

  // Check if Network Information API is available
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  
  if (!connection) {
    // If API not available, prefetch on desktop but not on mobile
    return !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  // Don't prefetch on slow connections
  if (connection.saveData) return false;
  
  // Only prefetch on fast connections (4g, wifi, etc)
  const effectiveType = connection.effectiveType;
  return effectiveType === '4g' || effectiveType === 'wifi';
}

/**
 * Prefetch common business data that's used across multiple pages.
 * Call this after authentication to warm up the cache.
 */
export async function prefetchBusinessData(businessId: string) {
  // Check network conditions before prefetching
  if (!shouldPrefetch()) {
    return;
  }

  const supabase = getBrowserClient();

  // Prefetch services (used on Home, Schedule, and booking modals)
  preload(swrKeys.services(businessId), async () => {
    const { data, error } = await supabase
      .from('services')
      .select('*')
      .eq('business_id', businessId)
      .eq('active', true)
      .order('name', { ascending: true });
    if (error) throw error;
    return data;
  });

  // Prefetch staff members (used on Home, Schedule, and booking modals)
  preload(swrKeys.staff(businessId), async () => {
    const { data, error } = await supabase
      .from('staff_members')
      .select(`*, profile:profiles(*)`)
      .eq('business_id', businessId)
      .eq('active', true)
      .order('display_name', { ascending: true });
    if (error) throw error;
    return data;
  });

  // Prefetch today's upcoming bookings (used on Home)
  preload(swrKeys.upcomingBookings(businessId, 5), async () => {
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('bookings')
      .select(`
        *,
        customer:customers(*),
        staff_member:staff_members(*),
        booking_services(*, service:services(*))
      `)
      .eq('business_id', businessId)
      .gte('start_at', now)
      .neq('status', 'cancelled')
      .order('start_at', { ascending: true })
      .limit(5);
    if (error) throw error;
    return data;
  });
}

/**
 * Prefetch schedule data for a specific date.
 * Call when user hovers over date navigation or is about to navigate.
 */
export async function prefetchScheduleForDate(businessId: string, date: Date, staffId?: string | null) {
  const supabase = getBrowserClient();
  const dateStr = date.toISOString().split('T')[0];
  const key = swrKeys.bookingsForDate(businessId, dateStr, staffId);

  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);

  preload(key, async () => {
    let query = supabase
      .from('bookings')
      .select(`
        *,
        customer:customers(*),
        staff_member:staff_members(*),
        booking_services(*, service:services(*))
      `)
      .eq('business_id', businessId)
      .gte('start_at', startOfDay.toISOString())
      .lte('start_at', endOfDay.toISOString())
      .neq('status', 'cancelled')
      .order('start_at', { ascending: true });

    if (staffId) {
      query = query.eq('staff_id', staffId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  });
}

/**
 * Prefetch adjacent dates for smooth navigation.
 * Call when schedule page loads to preload previous and next day/week/month.
 */
export function prefetchAdjacentDates(
  businessId: string, 
  currentDate: Date, 
  staffId?: string | null,
  viewMode: 'day' | 'week' | 'month' = 'day'
) {
  // Check network conditions before prefetching
  if (!shouldPrefetch()) {
    return;
  }

  const supabase = getBrowserClient();

  if (viewMode === 'day') {
    const prevDay = new Date(currentDate);
    prevDay.setDate(prevDay.getDate() - 1);
    
    const nextDay = new Date(currentDate);
    nextDay.setDate(nextDay.getDate() + 1);

    // Prefetch in background
    setTimeout(() => {
      prefetchScheduleForDate(businessId, prevDay, staffId);
      prefetchScheduleForDate(businessId, nextDay, staffId);
    }, 50); // Reduced from 100ms
  } else if (viewMode === 'week') {
    // Prefetch previous and next week
    const getWeekStart = (date: Date) => {
      const d = new Date(date);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      return new Date(d.setDate(diff));
    };

    const prevWeekStart = getWeekStart(currentDate);
    prevWeekStart.setDate(prevWeekStart.getDate() - 7);
    const prevWeekEnd = new Date(prevWeekStart);
    prevWeekEnd.setDate(prevWeekEnd.getDate() + 6);

    const nextWeekStart = getWeekStart(currentDate);
    nextWeekStart.setDate(nextWeekStart.getDate() + 7);
    const nextWeekEnd = new Date(nextWeekStart);
    nextWeekEnd.setDate(nextWeekEnd.getDate() + 6);

    setTimeout(() => {
      prefetchDateRange(businessId, prevWeekStart, prevWeekEnd, staffId);
      prefetchDateRange(businessId, nextWeekStart, nextWeekEnd, staffId);
    }, 50);
  } else if (viewMode === 'month') {
    // Prefetch previous and next month
    const prevMonth = new Date(currentDate);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    const prevMonthStart = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1);
    const prevMonthEnd = new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1, 0);

    const nextMonth = new Date(currentDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const nextMonthStart = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1);
    const nextMonthEnd = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0);

    setTimeout(() => {
      prefetchDateRange(businessId, prevMonthStart, prevMonthEnd, staffId);
      prefetchDateRange(businessId, nextMonthStart, nextMonthEnd, staffId);
    }, 100); // Slightly longer delay for month view (more data)
  }
}

/**
 * Prefetch schedule data for a date range.
 * Used for week and month view prefetching.
 */
export async function prefetchDateRange(
  businessId: string,
  startDate: Date,
  endDate: Date,
  staffId?: string | null
) {
  const supabase = getBrowserClient();
  const startStr = startDate.toISOString().split('T')[0];
  const endStr = endDate.toISOString().split('T')[0];
  const key = swrKeys.bookingsForDateRange(businessId, startStr, endStr, staffId);

  preload(key, async () => {
    let query = supabase
      .from('bookings')
      .select(`
        *,
        customer:customers(*),
        staff_member:staff_members(*),
        booking_services(*, service:services(*))
      `)
      .eq('business_id', businessId)
      .gte('start_at', startDate.toISOString())
      .lte('start_at', endDate.toISOString())
      .neq('status', 'cancelled')
      .order('start_at', { ascending: true });

    if (staffId) {
      query = query.eq('staff_id', staffId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  });
}

// ============================================================================
// Navigation Prefetching
// ============================================================================

type PrefetchTarget = 'home' | 'schedule' | 'customers' | 'sales' | 'inventory' | 'settings';

/**
 * Prefetch data for a target page on navigation hover.
 * Call this from navigation links on mouseenter/focus.
 *
 * @example
 * ```tsx
 * <Link
 *   href="/schedule"
 *   onMouseEnter={() => prefetchOnHover(businessId, 'schedule')}
 * >
 *   Schedule
 * </Link>
 * ```
 */
export function prefetchOnHover(businessId: string | null, target: PrefetchTarget) {
  if (!businessId || !shouldPrefetch()) return;

  switch (target) {
    case 'home':
      prefetchBusinessData(businessId);
      break;
    case 'schedule':
      prefetchScheduleForDate(businessId, new Date());
      break;
    case 'customers':
      prefetchCustomers(businessId);
      break;
    case 'sales':
      prefetchSalesSummary(businessId);
      break;
    case 'inventory':
      prefetchInventory(businessId);
      break;
    case 'settings':
      // Settings is mostly static, no prefetch needed
      break;
  }
}

/**
 * Prefetch customers data for the customers page.
 */
export async function prefetchCustomers(businessId: string) {
  if (!shouldPrefetch()) return;

  preload(swrKeys.customers(businessId), async () => {
    // Use the same function as the customers hook
    return getCustomersWithActivity(businessId);
  });
}

/**
 * Prefetch sales summary data for the sales page.
 */
export async function prefetchSalesSummary(businessId: string) {
  if (!shouldPrefetch()) return;

  const supabase = getBrowserClient();
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  // Prefetch today's sales summary
  const todayStart = new Date(today);
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(today);
  todayEnd.setHours(23, 59, 59, 999);

  preload(swrKeys.salesSummary(businessId, 'today', todayStr), async () => {
    const { data: paymentsData, error } = await supabase
      .from('payments')
      .select('*')
      .eq('business_id', businessId)
      .gte('created_at', todayStart.toISOString())
      .lte('created_at', todayEnd.toISOString())
      .order('created_at', { ascending: false });

    if (error) throw error;

    const payments = paymentsData || [];
    const completedPayments = payments.filter((p: any) => p.status === 'completed');

    return {
      totalRevenue: completedPayments.reduce((sum: number, p: any) => sum + Number(p.amount_aed), 0),
      cardPayments: completedPayments.filter((p: any) => p.method === 'card').reduce((sum: number, p: any) => sum + Number(p.amount_aed), 0),
      cashPayments: completedPayments.filter((p: any) => p.method === 'cash').reduce((sum: number, p: any) => sum + Number(p.amount_aed), 0),
      transferPayments: completedPayments.filter((p: any) => p.method === 'transfer').reduce((sum: number, p: any) => sum + Number(p.amount_aed), 0),
      transactionCount: completedPayments.length,
      transactions: payments.slice(0, 20),
      cacheStatus: 'MISS' as const,
    };
  });
}

/**
 * Prefetch inventory data for the inventory page.
 */
export async function prefetchInventory(businessId: string) {
  if (!shouldPrefetch()) return;

  const supabase = getBrowserClient();

  preload(`inventory-${businessId}`, async () => {
    const { data, error } = await supabase
      .from('inventory_products')
      .select('*')
      .eq('business_id', businessId)
      .order('name', { ascending: true });
    if (error) throw error;
    return data;
  });
}

/**
 * Prefetch data needed for the booking modal.
 * Call this when user clicks "New Booking" button before the modal opens.
 */
export function prefetchBookingModalData(businessId: string, staffId?: string) {
  if (!shouldPrefetch()) return;

  const supabase = getBrowserClient();

  // Services (usually already cached, but ensure)
  preload(swrKeys.services(businessId), async () => {
    const { data, error } = await supabase
      .from('services')
      .select('*')
      .eq('business_id', businessId)
      .eq('active', true)
      .order('name', { ascending: true });
    if (error) throw error;
    return data;
  });

  // Staff members
  preload(swrKeys.staff(businessId), async () => {
    const { data, error } = await supabase
      .from('staff_members')
      .select(`*, profile:profiles(*)`)
      .eq('business_id', businessId)
      .eq('active', true)
      .order('display_name', { ascending: true });
    if (error) throw error;
    return data;
  });

  // Business hours
  preload(swrKeys.businessHours(businessId), async () => {
    const { data, error } = await supabase
      .from('business_hours')
      .select('*')
      .eq('business_id', businessId)
      .order('day_of_week', { ascending: true });
    if (error) throw error;
    return data;
  });

  // Staff availability for today (if staffId provided)
  if (staffId) {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const dayStart = new Date(today);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(today);
    dayEnd.setHours(23, 59, 59, 999);

    preload(swrKeys.barberAvailability(businessId, staffId, todayStr), async () => {
      const { data, error } = await supabase
        .from('bookings')
        .select('start_at, end_at')
        .eq('business_id', businessId)
        .eq('staff_id', staffId)
        .lt('start_at', dayEnd.toISOString())
        .gt('end_at', dayStart.toISOString())
        .in('status', ['pending', 'confirmed', 'in_queue', 'in_progress'])
        .order('start_at', { ascending: true });
      if (error) throw error;
      return data || [];
    });
  }
}













